import { batchActions } from 'redux-batched-actions';
import { call, put, select } from 'redux-saga/effects';
import { updateArtboard } from 'containers/Artboards/actions';
import { activeArtboard } from 'containers/Artboards/selectors';
import { setFrozenEntityIdAction } from 'containers/FrozenAction/actions';
import { saveAppState } from 'containers/UndoRedoControl/actions';
import { LayoutMap } from 'models';
import { handleSagaError } from 'services/handleError';
import { Notifications } from 'services/Notifications';
import { intlGet } from 'utils/intlGet';
import { deleteLayouts } from '../actions';
import * as Models from '../models';
import { orderedActiveLayoutsBySectionId } from '../selectors';

export function* deleteCarouselElement(action: Models.Action.IDeleteCarouselElement): Generator {
  try {
    const { carouselId, sectionId } = action.payload;

    const { accepted }: { accepted: boolean } = yield call(
      [Notifications, Notifications.showConfirmation],
      {
        title: intlGet('ConfirmationWindow.Title', 'DeleteCarouselElement'),
        message: intlGet('ConfirmationWindow.Message', 'DeleteCarouselElement'),
        acceptLabel: intlGet('ConfirmationWindow.Button', 'DeleteCarouselItem'),
        declineLabel: intlGet('ConfirmationWindow.Button', 'Cancel'),
      },
    );

    if (!accepted) {
      return;
    }

    const artboard: ReturnType<typeof activeArtboard> = yield select(activeArtboard);
    const layoutsBySectionId: ReturnType<typeof orderedActiveLayoutsBySectionId> = yield select(orderedActiveLayoutsBySectionId);
    const sectionLayouts = layoutsBySectionId.get(sectionId);

    const carouselLayoutsToDelete = sectionLayouts?.filter((layout) => {
      const carouselItem = (layout as LayoutMap).get('carouselItem');

      return carouselItem?.get('carouselId') === carouselId;
    });

    if (!carouselLayoutsToDelete || carouselLayoutsToDelete.size === 0) {
      return;
    }

    const layoutIdsToDelete = carouselLayoutsToDelete.map(layout => (layout as LayoutMap).get('id')).toArray();

    const currentLayoutIds = artboard.get('layoutIds');
    const updatedLayoutIds = currentLayoutIds.filterNot(layoutId => layoutIdsToDelete.includes(layoutId));
    const updatedArtboard = artboard.set('layoutIds', updatedLayoutIds);

    yield put(batchActions([
      saveAppState(),
      deleteLayouts(layoutIdsToDelete),
      updateArtboard(updatedArtboard),
    ]));

    yield put(setFrozenEntityIdAction(null));
    yield call([Notifications, Notifications.success], intlGet('Notification.Success', 'CarouselElementDeleted'));
  } catch (error) {
    yield call([Notifications, Notifications.warn], intlGet('Notification.Error', 'CarouselElementCouldNotBeDeleted'));
    yield call(handleSagaError, error, 'Layouts.deleteCarouselElement', 'DeleteCarouselElement');
  }
}
